/** Header-action builder for the Email (Gmail-style inbox) view.
 *  Two modes: inbox-list (Filter, Mark-all-read, Refresh, Compose)
 *  and thread-open (Archive, Delete, Mark-unread, Star, Reply, Forward, Print,
 *  Compose). A new message stays available while reading mail.
 *  The filter pill's onClick toggles a popover whose open state lives in
 *  UnifiedAppHeader — it's passed in as a callback so the builder stays
 *  free of React state. All other actions fire window events. No JSX. */

import {
  SlidersHorizontal,
  CheckCircle2,
  RefreshCw,
  SquarePen,
  Archive as ArchiveIcon,
  Trash2,
  MailOpen,
  Star,
  Reply,
  Forward,
  Printer,
} from '@/shared/ui/icons';
import type { MobileHeaderAction } from './types';

export interface EmailHeaderCtx {
  emailThreadOpen: boolean;
  emailThreadStarred: boolean;
  emailFilterMenuOpen: boolean;
  emailActiveFiltersSize: number;
  onToggleFilterMenu: () => void;
  leadingActions: MobileHeaderAction[];
  trailingActions: MobileHeaderAction[];
  pushNotificationsAction: () => void;
  pushBackAction: (targetView: string, label: string, onClickOverride?: () => void) => void;
}

/** Appends Email pills to the caller's leading/trailing action arrays.
 *  Called only when `currentView === 'email'`. */
export function buildEmailHeaderActions(ctx: EmailHeaderCtx): void {
  const {
    emailThreadOpen,
    emailThreadStarred,
    emailFilterMenuOpen,
    emailActiveFiltersSize,
    onToggleFilterMenu,
    leadingActions: _leading,
    trailingActions,
    pushNotificationsAction,
    pushBackAction,
  } = ctx;

  // Thread-open mode: back chevron + message-level actions (archive, delete,
  // mark-unread, star, reply, forward, print). Inbox actions are suppressed.
  if (emailThreadOpen) {
    pushBackAction('email', 'Back to inbox', () =>
      window.dispatchEvent(new CustomEvent('email:back-to-list')),
    );
    trailingActions.push({
      id: 'email-thread-archive',
      icon: ArchiveIcon,
      ariaLabel: 'Archive',
      onClick: () => window.dispatchEvent(new CustomEvent('email:archive')),
      group: 'page',
    });
    trailingActions.push({
      id: 'email-thread-trash',
      icon: Trash2,
      ariaLabel: 'Delete',
      onClick: () => window.dispatchEvent(new CustomEvent('email:trash')),
      group: 'page',
    });
    trailingActions.push({
      id: 'email-thread-mark-unread',
      icon: MailOpen,
      ariaLabel: 'Mark as unread',
      onClick: () => window.dispatchEvent(new CustomEvent('email:mark-unread')),
      group: 'page',
    });
    trailingActions.push({
      id: 'email-thread-star',
      icon: Star,
      ariaLabel: emailThreadStarred ? 'Unstar' : 'Star',
      onClick: () => window.dispatchEvent(new CustomEvent('email:toggle-star')),
      active: emailThreadStarred,
      group: 'page',
    });
    trailingActions.push({
      id: 'email-thread-reply',
      icon: Reply,
      ariaLabel: 'Reply',
      onClick: () => window.dispatchEvent(new CustomEvent('email:reply')),
      group: 'page',
    });
    trailingActions.push({
      id: 'email-thread-forward',
      icon: Forward,
      ariaLabel: 'Forward',
      onClick: () => window.dispatchEvent(new CustomEvent('email:forward')),
      group: 'page',
    });
    trailingActions.push({
      id: 'email-thread-print',
      icon: Printer,
      ariaLabel: 'Print',
      onClick: () => window.dispatchEvent(new CustomEvent('email:print')),
      group: 'page',
    });
    trailingActions.push({
      id: 'email-compose',
      icon: SquarePen,
      ariaLabel: 'New message',
      onClick: () => window.dispatchEvent(new CustomEvent('email:compose')),
      group: 'page',
    });
  } else {
    // Inbox-list mode: back, then filter popover, mark-all-read, refresh,
    // compose. Search already has a pinned field above the message list.
    //
    // The back chevron is the ONLY way out of Email on a small screen: it is
    // not one of the bottom-nav tabs, and the sidebar that carries it on
    // desktop is hidden there and on the native shells. Without this the inbox
    // was a dead end — you could get in and not back out. Tasks and Resource
    // Library, the other top-level views reached the same way, already do this.
    pushBackAction('dashboard', 'Back to dashboard');

    trailingActions.push({
      id: 'email-filters',
      icon: SlidersHorizontal,
      ariaLabel: 'Filter',
      onClick: onToggleFilterMenu,
      active: emailFilterMenuOpen || emailActiveFiltersSize > 0,
      group: 'page',
    });
    trailingActions.push({
      id: 'email-mark-all',
      icon: CheckCircle2,
      ariaLabel: 'Mark all as read',
      onClick: () => window.dispatchEvent(new CustomEvent('email:mark-all-read')),
      group: 'page',
    });
    trailingActions.push({
      id: 'email-refresh',
      icon: RefreshCw,
      ariaLabel: 'Refresh',
      onClick: () => window.dispatchEvent(new CustomEvent('email:refresh')),
      group: 'page',
    });
    trailingActions.push({
      id: 'email-compose',
      icon: SquarePen,
      ariaLabel: 'New message',
      onClick: () => window.dispatchEvent(new CustomEvent('email:compose')),
      group: 'page',
    });
  }
  pushNotificationsAction();
}
